import { defineStore, storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import type { AppConfigDefinition, LanguageCodes } from '@/types'
import { useAppConfig } from '@/composables/useAppConfig';
import { normalizeCollectionModeForRequest } from '@/utils/collectionMode';

const appStateStore = defineStore('appState', () => {
	const currentRoute = useRoute()
	const router = useRouter()
	const AppConfig = useAppConfig()
	const runtimeAppConfig = AppConfig as AppConfigDefinition & {
		__customConfigSource?: Partial<AppConfigDefinition>
		__customConfigWindowKey?: string
		__rootSelector?: string
	}
	const supportedLanguageCodes = ['sq', 'en', 'fr', 'it', 'hu', 'de', 'auto'] as const

	const resolveLng = (value: unknown): LanguageCodes | null => {
		if (typeof value !== 'string') {
			return null
		}
		const normalized = value.trim().toLowerCase()
		return supportedLanguageCodes.includes(normalized as LanguageCodes)
			? (normalized as LanguageCodes)
			: null
	}

	const defaultLngKey = (source: string, value: unknown): string => {
		return `${source}:${String(value ?? '').trim().toLowerCase()}`
	}

	const readConfiguredDefaultLng = (): { lng: LanguageCodes | null; key: string } => {
		if (typeof window !== 'undefined') {
			const windowConfigKey = runtimeAppConfig.__customConfigWindowKey
			const windowConfig = windowConfigKey
				? (window as unknown as Record<string, Partial<AppConfigDefinition> | undefined>)[windowConfigKey]
				: undefined
			if (windowConfig && Object.prototype.hasOwnProperty.call(windowConfig, 'defaultLng')) {
				return {
					lng: resolveLng(windowConfig.defaultLng),
					key: defaultLngKey(`window-${windowConfigKey}`, windowConfig.defaultLng)
				}
			}

			if (window.MjcConfig && Object.prototype.hasOwnProperty.call(window.MjcConfig, 'defaultLng')) {
				return {
					lng: resolveLng(window.MjcConfig.defaultLng),
					key: defaultLngKey('window', window.MjcConfig.defaultLng)
				}
			}
		}

		const sourceConfig = runtimeAppConfig.__customConfigSource
		if (sourceConfig && Object.prototype.hasOwnProperty.call(sourceConfig, 'defaultLng')) {
			return {
				lng: resolveLng(sourceConfig.defaultLng),
				key: defaultLngKey('source-config', sourceConfig.defaultLng)
			}
		}

		if (Object.prototype.hasOwnProperty.call(runtimeAppConfig, 'defaultLng')) {
			return {
				lng: resolveLng(runtimeAppConfig.defaultLng),
				key: defaultLngKey('config', runtimeAppConfig.defaultLng)
			}
		}

		return { lng: null, key: 'none' }
	}

	const initialDefaultLng = readConfiguredDefaultLng()
	const configuredDefaultLng = ref<LanguageCodes | null>(initialDefaultLng.lng)
	const rememberedLng = ref<LanguageCodes | null>(resolveLng(currentRoute.query.lng))
	const userSelectedLng = ref(Boolean(resolveLng(currentRoute.query.lng)))
	const configuredDefaultLngKey = ref<string>(initialDefaultLng.key)
	const pendingSystemLng = ref<LanguageCodes | null | undefined>(undefined)

	const replaceRouteLngFromSystem = (lng: LanguageCodes | null) => {
		const nextQuery = { ...currentRoute.query }

		if (lng) {
			nextQuery.lng = lng
			rememberedLng.value = lng
			pendingSystemLng.value = lng
		} else {
			delete nextQuery.lng
			rememberedLng.value = null
			pendingSystemLng.value = null
		}

		userSelectedLng.value = false
		void router.replace({
			path: currentRoute.path,
			query: nextQuery,
		})
	}

	const refreshConfiguredDefaultLng = () => {
		const nextDefaultLng = readConfiguredDefaultLng()
		const configuredDefaultLngChanged = nextDefaultLng.key !== configuredDefaultLngKey.value

		if (!configuredDefaultLngChanged && nextDefaultLng.lng === configuredDefaultLng.value) {
			return
		}

		configuredDefaultLngKey.value = nextDefaultLng.key
		configuredDefaultLng.value = nextDefaultLng.lng
		if (configuredDefaultLngChanged) {
			replaceRouteLngFromSystem(nextDefaultLng.lng)
		}
	}

	if (typeof window !== 'undefined') {
		window.setInterval(refreshConfiguredDefaultLng, 250)
		window.addEventListener('storage', refreshConfiguredDefaultLng)
		window.addEventListener('mjc-config-updated', refreshConfiguredDefaultLng)
	}

	watch(
		() => currentRoute.query.lng,
		(value) => {
			const routeLng = resolveLng(value)
			if (routeLng) {
				rememberedLng.value = routeLng
				userSelectedLng.value = pendingSystemLng.value === routeLng ? false : true
				if (pendingSystemLng.value === routeLng) {
					pendingSystemLng.value = undefined
				}
				return
			}

			rememberedLng.value = null
			if (pendingSystemLng.value === null) {
				userSelectedLng.value = false
				pendingSystemLng.value = undefined
			}
		},
		{ immediate: true }
	)
	
	const currentLng = computed<LanguageCodes>({
		get: () => resolveLng(currentRoute.query.lng) ?? rememberedLng.value ?? configuredDefaultLng.value ?? ('' as LanguageCodes),
		set: (value: string) => {
			const nextLng = resolveLng(value) ?? configuredDefaultLng.value ?? ('en' as LanguageCodes)
			rememberedLng.value = nextLng
			userSelectedLng.value = true
			router.push({
				path: currentRoute.path,
				query: { ...currentRoute.query, lng: nextLng },
			})
		},
	})

	const syncLngFromSystem = (value: unknown) => {
		const nextLng = resolveLng(value)
		if (!nextLng || configuredDefaultLng.value || resolveLng(currentRoute.query.lng) || userSelectedLng.value) {
			return
		}
		rememberedLng.value = nextLng
	}

	const currentAuthoringFilter = computed({
		get: () => (currentRoute.query.authoringStatus as string) ?? AppConfig.authoringStatus ?? '',
		set: (value: string) => {
			router.push({
				path: currentRoute.path,
				query: { ...currentRoute.query, authoringStatus: value },
			})
		},
	})

	const currentPublishingFilter = computed({
		get: () => (currentRoute.query.publishingStatus as string) ?? AppConfig.publishingStatus ?? '',
		set: (value: string) => {
			router.push({
				path: currentRoute.path,
				query: { ...currentRoute.query, publishingStatus: value },
			})
		},
	})

	const currentMedicalFilter = computed({
		get: () => (currentRoute.query.medicalSpeciality as string) ?? AppConfig.medicalSpeciality ?? '',
		set: (value: string) => {
			router.push({
				path: currentRoute.path,
				query: { ...currentRoute.query, medicalSpeciality: value },
			})
		},
	})

	const currentStudyType = computed({
		get: () =>
			(currentRoute.query.studyType as string) ??
			(currentRoute.query.studyTpe as string) ??
			(currentRoute.query.studyMode as string) ??
			AppConfig.studyType ??
			'',
		set: (value: string) => {
			const nextQuery = { ...currentRoute.query } as Record<string, any>
			delete nextQuery.studyMode
			delete nextQuery.studyTpe
			router.push({
				path: currentRoute.path,
				query: { ...nextQuery, studyType: value },
			})
		},
	})



	const currentoutcomeType = computed({
		get: () => (currentRoute.query.outcomeType as string) ?? AppConfig.outcomeType ?? '',
		set: (value: string) => {
			router.push({
				path: currentRoute.path,
				query: { ...currentRoute.query, outcomeType: value },
			})
		},
	})

	const currentContentClassification = computed({
		get: () => (currentRoute.query.contentClassification as string) ?? AppConfig.contentClassification ?? '',
		set: (value: string) => {
			router.push({
				path: currentRoute.path,
				query: { ...currentRoute.query, contentClassification: value },
			})
		},
	})

	const currenttargetAudience = computed({
		get: () => (currentRoute.query.targetAudience as string) ?? AppConfig.targetAudience ?? '',
		set: (value: string) => {
			router.push({
				path: currentRoute.path,
				query: { ...currentRoute.query, targetAudience: value },
			})
		},
	})

	const currentaplicationDomain = computed({
		get: () => (currentRoute.query.aplicationDomain as string) ?? AppConfig.aplicationDomain ?? '',
		set: (value: string) => {
			router.push({
				path: currentRoute.path,
				query: { ...currentRoute.query, aplicationDomain: value },
			})
		},
	})

	const currentDataFilter = computed({
		get: () => (currentRoute.query['dashboard-filter'] as string) ?? AppConfig.dashboardFilter ?? '',
		set: (value: string) => {
			router.push({
				path: currentRoute.path,
				query: { ...currentRoute.query, 'dashboard-filter': value },
			});
		},
	});
	const currentstudyMode = computed({
		get: () => (currentRoute.query['flag-mode'] as string) ?? AppConfig.studyMode ?? '',
		set: (value: string) => {
			router.push({
				path: currentRoute.path,
				query: { ...currentRoute.query, 'flag-mode': value },
			});
		},
	});

	const currentLatestDays = computed({
		get: () => (currentRoute.query['latest-days'] as string) ?? AppConfig.latestDays ?? '',
		set: (value: string) => {
			router.push({
				path: currentRoute.path,
				query: { ...currentRoute.query, 'latest-days': value },
			});
		},
	});
	const currentTestyourself = computed({
		get: () => (currentRoute.query['testYourself'] as string) ?? AppConfig.testYourself ?? '',
		set: (value: string) => {
			router.push({
				path: currentRoute.path,
				query: { ...currentRoute.query, 'testYourself': value },
			});
		},
	});
	const currentCollectionMode = computed({
		get: () =>
			normalizeCollectionModeForRequest(
				(currentRoute.query['collection-mode'] as string) ?? AppConfig.collectionMode ?? 'audience'
			),
		set: (value: string) => {
			router.push({
				path: currentRoute.path,
				query: { ...currentRoute.query, 'collection-mode': normalizeCollectionModeForRequest(value) },
			});
		},
	});
	const currentPAGE_REQ_ID = computed({
		get: () => (currentRoute.query['PAGE_REQ_ID'] as string) ?? AppConfig.PAGE_REQ_ID ?? '',
		set: (value: string) => {
			router.push({
				path: currentRoute.path,
				query: { ...currentRoute.query, 'PAGE_REQ_ID': value },
			});
		},
	});

	return {
		currentLng,
		currentPublishingFilter,
		currentAuthoringFilter,
		currentMedicalFilter,
		currentStudyType,
		currentstudyMode,
		currentoutcomeType,
		currentContentClassification,
		currenttargetAudience,
		currentaplicationDomain,
		currentDataFilter,
		currentLatestDays,
		currentTestyourself,
		currentCollectionMode,
		currentPAGE_REQ_ID,
		syncLngFromSystem
	}
})

export const useAppStore = () => {
	const store = appStateStore()
	const refs = storeToRefs(store)

	return { ...store, ...refs }
}
